// cspell:ignore dayofweekiso

import type { CustomAnalyticsQueryTimeUnit } from "../../../semantic-queries/v1/shared/custom-analytics-query-primitives";

// Canonical Snowflake SQL vocabulary emitted by the custom analytics compiler.
// Admin debug syntax highlighting and display formatting import from here.
export const SnowflakeSqlKeyword = {
  And: "AND",
  As: "AS",
  Asc: "ASC",
  By: "BY",
  Case: "CASE",
  Desc: "DESC",
  Distinct: "DISTINCT",
  Else: "ELSE",
  End: "END",
  False: "FALSE",
  From: "FROM",
  GroupBy: "GROUP BY",
  Having: "HAVING",
  Ilike: "ILIKE",
  In: "IN",
  InnerJoin: "INNER JOIN",
  Is: "IS",
  IsDistinctFrom: "IS DISTINCT FROM",
  IsNotNull: "IS NOT NULL",
  IsNull: "IS NULL",
  LeftJoin: "LEFT JOIN",
  Like: "LIKE",
  Limit: "LIMIT",
  Not: "NOT",
  NotIn: "NOT IN",
  Null: "NULL",
  On: "ON",
  Or: "OR",
  OrderBy: "ORDER BY",
  Select: "SELECT",
  Then: "THEN",
  True: "TRUE",
  When: "WHEN",
  Where: "WHERE",
  With: "WITH",
  WithinGroup: "WITHIN GROUP",
} as const;

export const SnowflakeSqlFunction = {
  Abs: "ABS",
  AnyValue: "ANY_VALUE",
  ArrayAgg: "ARRAY_AGG",
  ArrayContains: "ARRAY_CONTAINS",
  ArraySize: "ARRAY_SIZE",
  ArraySlice: "ARRAY_SLICE",
  ArrayToString: "ARRAY_TO_STRING",
  Avg: "AVG",
  Ceil: "CEIL",
  Coalesce: "COALESCE",
  Concat: "CONCAT",
  ConvertTimezone: "CONVERT_TIMEZONE",
  Count: "COUNT",
  CurrentDate: "CURRENT_DATE",
  CurrentTimestamp: "CURRENT_TIMESTAMP",
  DateAdd: "DATEADD",
  DateDiff: "DATEDIFF",
  DatePart: "DATE_PART",
  DateTrunc: "DATE_TRUNC",
  Div0: "DIV0",
  Floor: "FLOOR",
  Greatest: "GREATEST",
  Iff: "IFF",
  LastDay: "LAST_DAY",
  Least: "LEAST",
  Length: "LENGTH",
  Lower: "LOWER",
  Lpad: "LPAD",
  Max: "MAX",
  Min: "MIN",
  Mod: "MOD",
  NullIf: "NULLIF",
  Power: "POWER",
  RegexpReplace: "REGEXP_REPLACE",
  Replace: "REPLACE",
  Right: "RIGHT",
  Round: "ROUND",
  Rpad: "RPAD",
  Sign: "SIGN",
  Split: "SPLIT",
  SplitPart: "SPLIT_PART",
  Substring: "SUBSTRING",
  Sum: "SUM",
  Sysdate: "SYSDATE",
  ToDate: "TO_DATE",
  ToTimestampNtz: "TO_TIMESTAMP_NTZ",
  Trim: "TRIM",
  Upper: "UPPER",
} as const;

export const SnowflakeSqlDataType = {
  Boolean: "BOOLEAN",
  Date: "DATE",
  Number: "NUMBER",
  String: "STRING",
  TimestampNtz: "TIMESTAMP_NTZ",
  Variant: "VARIANT",
} as const;

export const NativeSnowflakeFunctionSql = {
  abs: SnowflakeSqlFunction.Abs,
  cardinality: SnowflakeSqlFunction.ArraySize,
  ceil: SnowflakeSqlFunction.Ceil,
  char_length: SnowflakeSqlFunction.Length,
  coalesce: SnowflakeSqlFunction.Coalesce,
  concat: SnowflakeSqlFunction.Concat,
  current_utc_timestamp: SnowflakeSqlFunction.Sysdate,
  date_add: SnowflakeSqlFunction.DateAdd,
  date_diff: SnowflakeSqlFunction.DateDiff,
  date_trunc: SnowflakeSqlFunction.DateTrunc,
  divide_or_zero: SnowflakeSqlFunction.Div0,
  extract: SnowflakeSqlFunction.DatePart,
  floor: SnowflakeSqlFunction.Floor,
  greatest: SnowflakeSqlFunction.Greatest,
  if: SnowflakeSqlFunction.Iff,
  last_day: SnowflakeSqlFunction.LastDay,
  least: SnowflakeSqlFunction.Least,
  lower: SnowflakeSqlFunction.Lower,
  lpad: SnowflakeSqlFunction.Lpad,
  mod: SnowflakeSqlFunction.Mod,
  nullif: SnowflakeSqlFunction.NullIf,
  power: SnowflakeSqlFunction.Power,
  replace: SnowflakeSqlFunction.Replace,
  right: SnowflakeSqlFunction.Right,
  round: SnowflakeSqlFunction.Round,
  rpad: SnowflakeSqlFunction.Rpad,
  sign: SnowflakeSqlFunction.Sign,
  split_part: SnowflakeSqlFunction.SplitPart,
  substring: SnowflakeSqlFunction.Substring,
  trim: SnowflakeSqlFunction.Trim,
  upper: SnowflakeSqlFunction.Upper,
} as const;

export const SnowflakeTimeUnitSql = {
  hour: "hour",
  day: "day",
  dayofweek: "dayofweek",
  dayofyear: "dayofyear",
  week: "week",
  iso_dayofweek: "dayofweekiso",
  iso_week: "weekiso",
  month: "month",
  quarter: "quarter",
  year: "year",
} as const satisfies Record<CustomAnalyticsQueryTimeUnit, string>;

export const SnowflakeCompilerSqlDisplayClauseKeywords = [
  SnowflakeSqlKeyword.InnerJoin,
  SnowflakeSqlKeyword.LeftJoin,
  SnowflakeSqlKeyword.GroupBy,
  SnowflakeSqlKeyword.OrderBy,
  SnowflakeSqlKeyword.Select,
  SnowflakeSqlKeyword.From,
  SnowflakeSqlKeyword.Where,
  SnowflakeSqlKeyword.Having,
  SnowflakeSqlKeyword.Limit,
  SnowflakeSqlKeyword.With,
  SnowflakeSqlKeyword.On,
  SnowflakeSqlKeyword.And,
] as const;

const SnowflakeCompilerSqlPhrases = [
  ...Object.values(SnowflakeSqlKeyword),
  ...Object.values(SnowflakeSqlFunction),
  ...Object.values(SnowflakeSqlDataType),
] as const;

export const SnowflakeCompilerSqlTokenWords = [
  ...new Set(
    SnowflakeCompilerSqlPhrases.flatMap((phrase) =>
      phrase.toUpperCase().split(/\s+/),
    ),
  ),
];

export type SnowflakeCompilerSqlDisplayClauseKeyword =
  (typeof SnowflakeCompilerSqlDisplayClauseKeywords)[number];
